import React, { useCallback } from "react";
import { Redirect } from "react-router-dom";
import Button from "../../../atoms/button/Button";
import Input from "../../../atoms/form/input/Input";
import useForm from "../../../../hooks/useForm";
import InlineGroup from "../../../atoms/inlinegroup/InlineGroup";
import DisplayField from "../../../atoms/form/display-field/DisplayField";
import Form, { FormRequiredText } from "../../../atoms/form/Form";
import VerticalGroup from "../../../atoms/verticalgroup/VerticalGroup";
import Dropdown from "../../../atoms/form/input/Dropdown";
import Banner from "../../../atoms/banner/Banner";
import ClientDropdown from "../../../organisms/client-dropdown/ClientDropdown";
import { formatUserType } from "./lib/formatters";
import { useCurrentUser } from "../../../../context/userContext";

interface Props {
  user?: API.UserResponse;
  onSave: (data: API.UserRequest) => Promise<API.UserResponse>;
  onCancel: () => void;
}

export const ACCOUNT_TYPES = ["superadmin", "admin", "standard"].map(
  (type) => ({ value: type, label: formatUserType(type as API.Role) }),
);

function UserForm({ user, onSave, onCancel }: Props) {
  const currentUser = useCurrentUser();
  const [
    {
      formData: { email, firstName, lastName, type, state, clientIds },
      inProgress,
      fieldErrors,
      error,
      data,
      completed,
    },
    { setField, setDropdownField, setMultiSelectField, handleSubmit },
  ] = useForm(
    user
      ? {
          ...user,
        }
      : {
          email: "",
          firstName: "",
          lastName: "",
          type: "standard",
          state: "active",
          clientIds: [],
        },
  );

  const callback = useCallback(() => {
    const payload = {
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      type,
      state,
      clientIds,
    };
    return onSave(payload);
  }, [onSave, email, firstName, lastName, type, state, clientIds]);

  const validate = useCallback(() => {
    const errors = [];
    if (!email) {
      errors.push({ field: "email", message: "Enter an email" });
    }
    if (!firstName) {
      errors.push({ field: "firstName", message: "Enter a first name" });
    }

    if (!lastName) {
      errors.push({ field: "lastName", message: "Enter a last name" });
    }

    if (type !== "superadmin" && !clientIds.length) {
      errors.push({
        field: "clientIds",
        message: "Please select at least one client",
      });
    }

    return errors;
  }, [email, firstName, lastName, clientIds, type]);

  return (
    <VerticalGroup spread full>
      <VerticalGroup wide>
        <Banner type="error" active={!!error} message={error?.message} />
        {!!completed && !error && (
          <Redirect to={`/administration/users/${data.id}/view`} />
        )}
        <Form id="user-add-form" onSubmit={handleSubmit(callback, validate)}>
          <FormRequiredText />
          <Input
            autoFocus
            data-test="email"
            type="text"
            label="Email *"
            value={email}
            onChange={setField("email")}
            error={fieldErrors.email}
          />
          <Input
            data-test="firstName"
            type="text"
            label="First Name *"
            value={firstName}
            onChange={setField("firstName")}
            error={fieldErrors.firstName}
          />
          <Input
            data-test="lastName"
            type="text"
            label="Last Name *"
            value={lastName}
            onChange={setField("lastName")}
            error={fieldErrors.lastName}
          />
          <Dropdown
            label="Account Type"
            selectProps={{
              options:
                currentUser?.type === "superadmin"
                  ? ACCOUNT_TYPES
                  : ACCOUNT_TYPES.filter((type) => type.value !== "superadmin"),
              defaultValue: ACCOUNT_TYPES.find((p) => p.value === type),
              onChange: setDropdownField("type"),
            }}
          />
          {type !== "superadmin" && (
            <ClientDropdown
              label="Select Clients *"
              isMulti
              clientIds={clientIds}
              onChange={setMultiSelectField("clientIds")}
              error={fieldErrors.clientIds}
            />
          )}
          {!!user && <DisplayField label="User ID" value={user.id} />}
        </Form>
      </VerticalGroup>
      <InlineGroup block spread>
        <Button data-test="cancel" light wide onClick={onCancel}>
          Cancel
        </Button>
        <Button
          data-test="save"
          type="submit"
          form="user-add-form"
          wide
          inProgress={inProgress}
        >
          Save
        </Button>
      </InlineGroup>
    </VerticalGroup>
  );
}

export default UserForm;
